import { useEffect, type FC, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UiGrid } from '@/lib/ui';
import Ticket from './Ticket';
import { useRegisterRoute } from '@/registration/hook/useRegisterRoute';
import { type TicketType } from '@/api/constant';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { type TicketListData, loadTicketListByAttendeeCategory, ticketListQueryKey } from '@/api/registration';
import { type GroupRegistration } from '@/api/registration/groupRegistration';
import { CartTempRegistration, useGroupShoppingCart } from '@/registration/hook/useGroupShoppingCart';

export interface TicketListProps {
  ticketType: TicketType
  onEmpty?: () => void
  groupRegistration: GroupRegistration
}

function groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
  return array.reduce<Record<string, T[]>>((result, currentValue) => {
    const groupKey = currentValue[key] as unknown as string;
    if (!result[groupKey]) {
      result[groupKey] = [];
    }
    result[groupKey].push(currentValue);
    return result;
  }, {});
}

const TicketList: FC<TicketListProps> = ({ ticketType, onEmpty, groupRegistration }) => {
  const { eventId } = useRegisterRoute();
  const attendeeCategoryId = groupRegistration?.attendeeCategoryId;
  const { createTenantApiRequest, isLoading: isApiPreparing } = useTenantApi();
  const { cartTempRegistrationList, isLoading: isGroupShoppingCartLoading, getTicketTempRegistrationList } = useGroupShoppingCart(eventId);

  const ticketQuery = useQuery<TicketListData, Error>({
    queryKey: [ticketListQueryKey, { eventId, ticketType, attendeeCategoryId }],
    queryFn: async () => {
      return (
        await loadTicketListByAttendeeCategory(createTenantApiRequest)({
          eventId,
          attendeeCategoryId,
          ticketType,
          groupRegistrationId: groupRegistration.id
        })).items;
    },
    enabled: !!attendeeCategoryId && !isApiPreparing
  });

  useEffect(() => {
    if (!ticketQuery.isLoading && !ticketQuery.data?.length) {
      onEmpty?.();
    }
  }, [ticketQuery.isLoading, ticketQuery.data, onEmpty]);

  // const getTicketRegistrationList = useCallback((ticketId: number): CartTempRegistration[] => {
  //   const _list: CartTempRegistration[] = [];
  //   for (const tempRegoCartInfo of cartTempRegistrationList) {
  //     if (tempRegoCartInfo.ticketIds.includes(ticketId)) {
  //       _list.push(tempRegoCartInfo);
  //     }
  //   }
  //   return _list;
  // }, [
  //   cartTempRegistrationList,
  // ]);

  return (
    <QueryContainer query={ticketQuery}>
      {tickets => {
        const groupedBySharedStock = groupBy(tickets, 'ticketSharedStockId');
        return (
          <UiGrid
            templateColumns={{ base: '1fr', lg: '1fr 1fr' }}
            gap={8}
          >
            {tickets.map((ticket) => {
              const sharedTicketIds = ticket.ticketSharedStockId ? groupedBySharedStock[ticket.ticketSharedStockId].map(({ id }) => { return id; }) : [];
              return (
                <Ticket
                  key={ticket.id}
                  ticket={ticket}
                  sharedTicketIds={sharedTicketIds}
                  // isAdded={hasTicket(ticket.id ?? 0)}
                  tempRegistrationList={getTicketTempRegistrationList(ticket)}
                />
              );
            })}
          </UiGrid>
        );
      }}
    </QueryContainer>
  );
};

export default TicketList;
